import React, { useEffect, useState } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { isValidFormInputString, isValidZipCode } from 'shared';
import { IAddressBody } from 'api/UserApi';
import { FieldGroup, Grid, Stack, StateInput, TextInput } from 'library/ui';
import FormSection from './FormSection';

interface AddressSectionProps {
    setValid: (valid: boolean) => void;
    setAddressPatchBody: (body: IAddressBody) => void;
    expanded: boolean;
    complete: boolean;
    onToggle: () => void;
    onNext: () => void;
}

export default function AddressSection({
    setValid,
    setAddressPatchBody,
    expanded,
    complete,
    onToggle,
    onNext,
}: AddressSectionProps) {
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [zip, setZip] = useState('');
    const [state, setState] = useState<string | null>(null);

    useEffect(() => {
        const valid =
            isValidFormInputString(street ?? '') &&
            !!state &&
            isValidFormInputString(city ?? '') &&
            isValidZipCode(zip ?? '') &&
            isValidFormInputString(state ?? '');
        if (street && city && zip && state) {
            setAddressPatchBody({ street, state, city, zip_code: zip });
        }
        setValid(valid);
    }, [street, city, zip, state, setValid]);

    return (
        <FormSection
            title="Location"
            subtitle="Enter the address of your childcare center"
            icon={<LocationOnIcon />}
            expanded={expanded}
            complete={complete}
            onToggle={onToggle}
            onNext={onNext}
            autoFocusId="street"
        >
            <Stack gap="md" fullWidth>
                <FieldGroup label="Center Street Address*">
                    <TextInput
                        id="street"
                        value={street}
                        onChange={setStreet}
                        error={
                            street !== '' && !isValidFormInputString(street) ? 'Please enter your street address' : ''
                        }
                        fullWidth
                    />
                </FieldGroup>
                <FieldGroup label="City*">
                    <TextInput
                        id="city"
                        value={city}
                        onChange={setCity}
                        error={city !== '' && !isValidFormInputString(city) ? 'Please enter your city' : ''}
                        fullWidth
                    />
                </FieldGroup>
                <Grid minColumnWidth={200} gap="md">
                    <FieldGroup label="State*">
                        <StateInput id="state-input" value={state} onChange={setState} fullWidth />
                    </FieldGroup>
                    <FieldGroup label="Zip*">
                        <TextInput
                            id="zip"
                            value={zip}
                            onChange={setZip}
                            error={zip !== '' && !isValidZipCode(zip) ? 'Please enter your zip code' : ''}
                            fullWidth
                        />
                    </FieldGroup>
                </Grid>
            </Stack>
        </FormSection>
    );
}
